import { Stage } from './stage.ts';
import { AudioEngine } from './audio.ts';
import { Game, isVoicePromptActive } from './game.ts';
import { stories, type Story, type VoiceKey } from './script.ts';
import { track } from './analytics.ts';
import { fxSettings } from './settings.ts';
import { attachCustomCursor } from './cursor.ts';
// Cursor-state debug HUD. Re-enable for diagnosing dual-cursor or
// stuck-cursor issues; left in the codebase so it's a one-line flip.
// import { attachCursorDebug } from './cursor-debug.ts';
import { ParserEngine } from './parser/parser-engine.ts';
import { hollowHoursWorld } from './parser/hollow-hours-world.ts';
import { initLoadingScreen } from './loading-screen.ts';
import './styles.css';

// Boot the loading-screen controller before anything else so the
// progress bar starts ticking and the overlay dismisses the moment
// the initial <img> tags settle. The overlay markup itself is
// already on screen — see index.html.
initLoadingScreen();

attachCustomCursor();
// attachCursorDebug();

track('page_loaded', { path: location.pathname });

const canvas = document.getElementById('stage') as HTMLCanvasElement;
const startBtn = document.getElementById('start') as HTMLButtonElement;

const stage = new Stage(canvas);
stage.start();

const audio = new AudioEngine();
const game = new Game(stage, audio);

const startHint = document.getElementById('start-hint')!;
let micPermStatus: PermissionStatus | null = null;

// One-time main-menu audio bootstrap. Fires on the very first user gesture
// anywhere on the page (click, key, touch) — browsers require a user gesture
// before AudioContext can exist, so this is the earliest legal moment.
// Returns a shared promise so concurrent callers all await the same boot.
let menuAudioBootPromise: Promise<void> | null = null;
function bootMenuAudio(): Promise<void> {
  if (menuAudioBootPromise) return menuAudioBootPromise;
  menuAudioBootPromise = (async () => {
    await audio.init();
    await audio.loadMany([
      { key: 'ui_hover',   url: '/audio/sfx/ui_hover.mp3' },
      { key: 'ui_click',   url: '/audio/sfx/ui_click.mp3' },
      { key: 'music_main', url: '/audio/sfx/music_main.mp3' },
    ]);
    audio.startMusic('music_main', 0.35);
  })();
  return menuAudioBootPromise;
}
document.addEventListener('pointerdown', () => { void bootMenuAudio(); }, { once: true });
document.addEventListener('keydown',     () => { void bootMenuAudio(); }, { once: true });

/** Watch the browser's mic permission state. Once the user changes it in
 *  browser settings (e.g. via the address-bar lock icon), reset the start
 *  button so they can click it without a manual reload. */
async function watchMicPermission() {
  if (micPermStatus || !navigator.permissions) return;
  try {
    micPermStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    micPermStatus.onchange = () => {
      if (micPermStatus && micPermStatus.state !== 'denied') {
        startBtn.disabled = false;
        startBtn.textContent = 'Take your seat';
        startHint.classList.add('hidden');
      }
    };
  } catch {
    /* Permissions API not supported / 'microphone' not in PermissionName */
  }
}

// Story picker — populated once from the stories registry.
const menuLinksEl = document.getElementById('menu-links')!;
const pickerEl = document.getElementById('story-picker')!;
const pickerCardsEl = document.getElementById('picker-cards')!;
const pickerCancelBtn = document.getElementById('picker-cancel') as HTMLButtonElement;
const menuSettingsBtn = document.getElementById('menu-settings') as HTMLButtonElement;

/** Stories playable right now. Anything else in the registry renders
 *  in the picker as a "coming soon" card — visible, greyed out,
 *  unclickable. Edit this list as more stories ship. */
const AVAILABLE_STORIES: ReadonlySet<string> = new Set(['hollow_hours']);

function buildPicker() {
  pickerCardsEl.innerHTML = '';
  for (const [id, story] of Object.entries(stories)) {
    const isAvailable = AVAILABLE_STORIES.has(id);
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'picker-card' + (isAvailable ? '' : ' disabled');
    card.dataset.story = id;
    card.disabled = !isAvailable;
    const badge = isAvailable
      ? `<div class="picker-card-tag">story</div>`
      : `<div class="picker-card-tag picker-card-soon">coming soon</div>`;
    card.innerHTML = `
      ${badge}
      <p class="picker-card-title">${escapeHtml(story.title)}</p>
      <p class="picker-card-desc">${escapeHtml(story.description)}</p>
    `;
    if (isAvailable) {
      card.addEventListener('click', () => beginStory(story));
    }
    pickerCardsEl.appendChild(card);
  }
}
function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
buildPicker();

function showPicker() {
  pickerEl.classList.remove('hidden');
  // Hide the Take-Your-Seat button entirely while the picker is up —
  // the picker takes its place visually and the button shouldn't peek
  // out from behind the picker card.
  startBtn.classList.add('hidden');
}
function hidePicker() {
  pickerEl.classList.add('hidden');
  startBtn.classList.remove('hidden');
}

pickerCancelBtn.addEventListener('click', () => {
  hidePicker();
  startBtn.disabled = false;
  startBtn.textContent = 'Take your seat';
});

menuSettingsBtn.addEventListener('click', () => {
  settingsEl.classList.toggle('hidden');
});

// Start button — opens the picker. Each picker card calls beginStory().
startBtn.addEventListener('click', () => {
  showPicker();
});

let beginInFlight = false;
async function beginStory(story: Story) {
  if (beginInFlight) return;
  beginInFlight = true;
  track('story_picker_selected', { storyId: story.id });
  hidePicker();
  startBtn.disabled = true;
  startBtn.textContent = 'requesting microphone…';
  startHint.classList.add('hidden');
  try {
    await bootMenuAudio();
    await audio.requestMicrophone();
  } catch (err) {
    console.warn('[main] microphone access denied or failed', err);
    const e = err as { name?: string };
    const isDenial = e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError';
    startBtn.disabled = false;
    if (isDenial) {
      startBtn.textContent = 'mic blocked';
      startHint.innerHTML = 'Microphone is blocked. Click the <b>🔒 lock</b> ' +
        '(or camera/mic icon) in your address bar, set <b>Microphone</b> to ' +
        '<b>Allow</b>, then click <b>Take your seat</b> again. ' +
        'Your browser won\'t show the popup again until permission is reset.';
      startHint.classList.remove('hidden');
      void watchMicPermission();
    } else {
      startBtn.textContent = 'microphone required — click to retry';
    }
    beginInFlight = false;
    return;
  }
  startBtn.textContent = 'loading…';
  // Splat is no longer loaded eagerly — it's a heavy gaussian-splat scene
  // that's only fetched if the user toggles "show gaussian splat" in the
  // debug panel (see splatCheckbox handler below).
  await game.preload();
  startBtn.classList.add('hidden');
  startHint.classList.add('hidden');
  menuLinksEl.classList.add('hidden');
  // Mark the play as started so the audience overlay fades in
  // (CSS gates it on body.play-started).
  document.body.classList.add('play-started');
  // Reveal the paper clouds-on-sticks hanging from the ceiling now that
  // the play is starting (they're hidden during the start menu).
  const ceilingProps = document.getElementById('ceiling-props');
  if (ceilingProps) {
    ceilingProps.classList.remove('hidden');
    void ceilingProps.offsetWidth;
    ceilingProps.classList.add('visible');
  }
  audio.setRoomTone(1.0);

  // Show the controls card once before the actual play begins. The
  // player has to click "begin →" to dismiss; after that, ESC toggles
  // the same card during the play so they can re-read the controls
  // any time without restarting.
  await showHelpModal({ withContinue: true });

  if (story.id === 'hollow_hours') {
    // Parser-CYOA route — free text, gated exits, inventory cards. The
    // legacy Game instance is still used for showEnding so we keep the
    // same end-screen UI for both paths.
    const engine = new ParserEngine(hollowHoursWorld, stage, audio);
    const ending = await engine.run();
    await game.showEnding(ending);
    return;
  }

  game.setStory(story);
  await game.play();
}

// ── Help / controls modal ─────────────────────────────────────────────
// Shown once before the play starts (with a "begin →" button that gates
// the actual game loop), and any time the player hits ESC during play
// (toggle on/off). The modal element + button live in index.html.
const helpModalEl = document.getElementById('help-modal')!;
const helpContinueBtn = document.getElementById('help-continue') as HTMLButtonElement;
let helpModalOpen = false;

function setHelpModalOpen(open: boolean, withContinue: boolean) {
  helpModalOpen = open;
  helpContinueBtn.classList.toggle('hidden', !withContinue);
  helpModalEl.classList.toggle('hidden', !open);
}

/** Show the help card; if `withContinue`, await the player's click on
 *  "begin →" before resolving (used at the start of a play). Otherwise
 *  resolves immediately (used by the ESC toggle during play). */
function showHelpModal(opts: { withContinue: boolean }): Promise<void> {
  setHelpModalOpen(true, opts.withContinue);
  if (!opts.withContinue) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onClick = () => {
      helpContinueBtn.removeEventListener('click', onClick);
      setHelpModalOpen(false, false);
      resolve();
    };
    helpContinueBtn.addEventListener('click', onClick);
  });
}

// ESC toggles the help card during play. Only reacts once the play has
// started (so it doesn't fight the picker or the start menu), and only
// when the modal isn't already in its first-show "begin" state — that
// state is exited via the button, not ESC.
window.addEventListener('keydown', e => {
  if (e.code !== 'Escape') return;
  if (!document.body.classList.contains('play-started')) return;
  if (!helpContinueBtn.classList.contains('hidden')) return;
  setHelpModalOpen(!helpModalOpen, false);
});

// Voice-input meter — V key opens the mic AND the meter; releasing closes
// both. The actual MediaStream tracks are stopped on release so the OS mic
// indicator goes off. Mic only ever active while V is held down.
const voiceMeterEl = document.getElementById('voice-meter')!;
const voiceMeterFill = voiceMeterEl.querySelector('.voice-meter-fill') as HTMLElement;
const voiceMeterLabel = voiceMeterEl.querySelector('.voice-meter-label') as HTMLElement;
let vDown = false;
window.addEventListener('keydown', e => {
  if (e.code === 'KeyV' && !vDown && !e.repeat) {
    vDown = true;
    voiceMeterEl.classList.remove('hidden');
    void voiceMeterEl.offsetWidth;
    voiceMeterEl.classList.add('active');
    voiceMeterLabel.textContent = '🎙 listening — speak';
    // Skip mic management when a voice_prompt is active — game.ts owns the
    // mic lifecycle there so it can collect the recorded blob for STT.
    if (!isVoicePromptActive()) {
      void audio.openMic().catch(err => {
        console.warn('[main] V pressed but openMic failed', err);
      });
    }
  }
});
window.addEventListener('keyup', e => {
  if (e.code === 'KeyV') {
    vDown = false;
    voiceMeterEl.classList.remove('active');
    setTimeout(() => { if (!vDown) voiceMeterEl.classList.add('hidden'); }, 220);
    if (!isVoicePromptActive()) {
      void audio.closeMic();
    }
  }
});
// UI hover/click chimes — only after audio.init() has completed (after the
// first user gesture), and debounced so each element only fires hover once
// per entry, not per pixel of mouse movement.
const UI_SELECTOR = 'button, .choice, #settings label';
let lastHoverEl: Element | null = null;
document.addEventListener('mouseover', e => {
  const el = (e.target as Element).closest?.(UI_SELECTOR) ?? null;
  if (!el || el === lastHoverEl) return;
  lastHoverEl = el;
  void audio.playSfx('ui_hover', { volume: 0.35 });
});
document.addEventListener('mouseout', e => {
  const el = (e.target as Element).closest?.(UI_SELECTOR) ?? null;
  if (el === lastHoverEl) lastHoverEl = null;
});
document.addEventListener('click', e => {
  const el = (e.target as Element).closest?.(UI_SELECTOR);
  if (!el) return;
  void audio.playSfx('ui_click', { volume: 0.5 });
});

function tickVoiceMeter() {
  if (vDown) {
    const amp = audio.getMicAmplitude();
    // Scale 0..0.5 RMS into 0..100% — typical speech sits in 0.05..0.25.
    voiceMeterFill.style.width = `${Math.min(100, amp * 280)}%`;
  }
  requestAnimationFrame(tickVoiceMeter);
}
tickVoiceMeter();

// Expose for live tuning of the splat transform from devtools.
(window as unknown as { stage: Stage }).stage = stage;

// Settings panel — P key opens/closes; checkboxes wire to Stage.
const settingsEl = document.getElementById('settings')!;
const splatCheckbox = document.getElementById('setting-splat') as HTMLInputElement;
const paperCheckbox = document.getElementById('setting-paper') as HTMLInputElement;
const cutsCheckbox = document.getElementById('setting-cuts') as HTMLInputElement;
const rotateCheckbox = document.getElementById('setting-rotate') as HTMLInputElement;
const prosceniumCheckbox = document.getElementById('setting-proscenium') as HTMLInputElement;
const lowerAudienceCheckbox = document.getElementById('setting-lower-audience') as HTMLInputElement;
const audienceOverlayEl = document.getElementById('audience-overlay')!;
const cloudWobbleCheckbox = document.getElementById('setting-cloud-wobble') as HTMLInputElement;
const cloudParadeCheckbox = document.getElementById('setting-cloud-parade') as HTMLInputElement;
const ceilingPropsEl = document.getElementById('ceiling-props')!;
// Force defaults on every load — browsers persist checkbox state across reloads.
splatCheckbox.checked = false;
paperCheckbox.checked = false;
cutsCheckbox.checked = false;
rotateCheckbox.checked = false;
prosceniumCheckbox.checked = false;
stage.setSplatVisible(false);
stage.setPaperEffect(false);
stage.setCameraCuts(false);
stage.setRotateSplat(false);
stage.setProsceniumVisible(false);

window.addEventListener('keydown', e => {
  if (e.code === 'KeyP') settingsEl.classList.toggle('hidden');
});

// Splat is heavy — lazy-load on first enable. After that, the checkbox
// just toggles visibility on the already-loaded mesh.
let splatLoadPromise: Promise<void> | null = null;
splatCheckbox.addEventListener('change', () => {
  if (splatCheckbox.checked) {
    if (!splatLoadPromise) {
      splatLoadPromise = stage.loadSplat().catch(err => {
        console.warn('[main] splat load failed', err);
        splatLoadPromise = null;
      });
    }
    splatLoadPromise.then(() => stage.setSplatVisible(true));
  } else {
    stage.setSplatVisible(false);
  }
});
paperCheckbox.addEventListener('change', () => stage.setPaperEffect(paperCheckbox.checked));
cutsCheckbox.addEventListener('change', () => stage.setCameraCuts(cutsCheckbox.checked));
rotateCheckbox.addEventListener('change', () => stage.setRotateSplat(rotateCheckbox.checked));
prosceniumCheckbox.addEventListener('change', () => stage.setProsceniumVisible(prosceniumCheckbox.checked));
lowerAudienceCheckbox.checked = false;
audienceOverlayEl.classList.remove('lowered');
lowerAudienceCheckbox.addEventListener('change', () => {
  audienceOverlayEl.classList.toggle('lowered', lowerAudienceCheckbox.checked);
});

// Cloud animation modes — wobble (idle) and parade (drift across).
// parade overrides wobble when both are toggled on.
cloudWobbleCheckbox.checked = true;
cloudParadeCheckbox.checked = true;
function syncCloudClasses() {
  const parade = cloudParadeCheckbox.checked;
  const wobble = cloudWobbleCheckbox.checked;
  ceilingPropsEl.classList.toggle('parade', parade);
  ceilingPropsEl.classList.toggle('no-wobble', !parade && !wobble);
  if (!parade) clearSpawnedClouds();
}
syncCloudClasses();
cloudWobbleCheckbox.addEventListener('change', syncCloudClasses);
cloudParadeCheckbox.addEventListener('change', syncCloudClasses);

// ── Cloud parade spawner ──────────────────────────────────────────────────
// Maintains 1–4 active spawned clouds, mixing directions. Each cloud is a
// freshly-created element that runs cloud-parade-lr/rl once and removes
// itself. Forces a spawn the moment active==0 so there's never a long dead
// stretch; otherwise spawns at random 6–14s intervals up to a cap of 4.
type ActiveCloud = { el: HTMLElement; removeAt: number };
const activeClouds: ActiveCloud[] = [];
const CLOUD_FLOOR = 2;   // below this we spawn aggressively
const CLOUD_CAP = 4;     // never exceed this
let lastSpawnDir: 'lr' | 'rl' | null = null;

function spawnCloud() {
  if (activeClouds.length >= CLOUD_CAP) return;
  // Bias direction toward the opposite of the last spawn so we don't get
  // a long unbroken parade in a single direction.
  const dir: 'lr' | 'rl' = lastSpawnDir === 'lr'
    ? (Math.random() < 0.75 ? 'rl' : 'lr')
    : lastSpawnDir === 'rl'
    ? (Math.random() < 0.75 ? 'lr' : 'rl')
    : (Math.random() < 0.5 ? 'lr' : 'rl');
  lastSpawnDir = dir;

  const widthVmin    = 16.5 + Math.random() * 9;        // 16.5–25.5 vmin (1.5× of 11–17)
  const stickMult    = 1.5 + Math.random() * 0.5;       // 150%–200%
  const stickHeightVh = 27 * stickMult;                 // 40.5–54 vh
  const peekMs       = 2200 + Math.random() * 900;      // 2.2–3.1 s
  // Where the cloud peeks down from.
  //   Full-width mode (default): peek anywhere across 5..95vw.
  //   Side-only mode: peek at the parade-entry edge (LR -5..18vw,
  //     RL 82..105vw) so the parade has the full screen to traverse.
  const peekX = fxSettings.cloudPeekFullWidth
    ? 5 + Math.random() * 90
    : (dir === 'lr' ? -5 + Math.random() * 23 : 82 + Math.random() * 23);

  // Distance the cloud needs to travel from peek-x in the parade
  // direction to be fully offscreen. EXIT_MARGIN_VW is how far past the
  // viewport edge the prop's center must reach so the cloud + stick are
  // entirely off-screen. Parade duration is then derived from this so
  // the animation ends EXACTLY when the cloud disappears — no time
  // wasted with the cloud stuck offscreen.
  const EXIT_MARGIN_VW = 28;
  const paradeDistVw = dir === 'lr'
    ? (100 - peekX + EXIT_MARGIN_VW)
    : (peekX + EXIT_MARGIN_VW);
  // Drift velocity (vw per second). Random per cloud for visual variety.
  const velocityVwPerSec = 4.0 + Math.random() * 2.5;   // 4–6.5 vw/s
  const paradeMs = (paradeDistVw / velocityVwPerSec) * 1000;

  const el = document.createElement('div');
  el.className = `ceiling-prop spawned dir-${dir}`;
  el.style.setProperty('--w',           `${widthVmin.toFixed(2)}vmin`);
  el.style.setProperty('--stick-h',     `${stickHeightVh.toFixed(2)}vh`);
  el.style.setProperty('--dur',         `${paradeMs.toFixed(0)}ms`);
  el.style.setProperty('--peek-dur',    `${peekMs.toFixed(0)}ms`);
  el.style.setProperty('--peek-x',      `${peekX.toFixed(2)}vw`);
  el.style.setProperty('--parade-dist', `${paradeDistVw.toFixed(2)}vw`);
  el.innerHTML =
    '<img class="cp-stick"  src="/img/Stick%20No%20Background.png" alt="" />' +
    '<img class="cp-cloud"  src="/img/cloud.png" alt="" />';
  ceilingPropsEl.appendChild(el);

  const expectedTotalMs = peekMs + paradeMs;
  // `removeAt` is now only used by activeClouds bookkeeping for density
  // logic; actual element removal is gated on the geometric check below.
  const removeAt = performance.now() + expectedTotalMs;
  const entry: ActiveCloud = { el, removeAt };
  activeClouds.push(entry);

  // Bulletproof despawn: never remove the element while ANY part of it
  // is intersecting the viewport. We start polling at the animation's
  // expected end and keep checking until the bounding boxes of the
  // cloud img AND the stick img are both fully off-screen. A hard
  // failsafe (3× expected duration + 30s) prevents a zombie cloud if
  // something pathological happens — better a delayed cleanup than a
  // visible pop-out.
  const startedPolling = performance.now();
  const FAILSAFE_MS = expectedTotalMs * 3 + 30000;
  const tryDespawn = () => {
    if (!el.isConnected) return;     // already removed
    const elapsed = performance.now() - startedPolling;
    if (elapsed > FAILSAFE_MS || isCloudFullyOffscreen(el)) {
      el.remove();
      const i = activeClouds.indexOf(entry);
      if (i >= 0) activeClouds.splice(i, 1);
      return;
    }
    setTimeout(tryDespawn, 200);
  };
  // Wait until the animation should have finished, plus a small grace
  // period for the final keyframe to render, before the first check.
  setTimeout(tryDespawn, expectedTotalMs + 250);
}

/** Returns true only when EVERY visible piece of the cloud prop (the
 *  cloud img and the stick img) is fully outside the viewport rect.
 *  Checking the wrapper alone isn't enough — its absolutely-positioned
 *  children can extend beyond the wrapper's own bbox. */
function isCloudFullyOffscreen(propEl: HTMLElement): boolean {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const parts = propEl.querySelectorAll<HTMLElement>('.cp-cloud, .cp-stick');
  for (const part of parts) {
    const r = part.getBoundingClientRect();
    // Skip zero-size (image not yet loaded / hidden).
    if (r.width === 0 && r.height === 0) continue;
    const onScreen = r.right > 0 && r.left < W && r.bottom > 0 && r.top < H;
    if (onScreen) return false;
  }
  return true;
}

function clearSpawnedClouds() {
  for (const c of activeClouds) c.el.remove();
  activeClouds.length = 0;
}

let nextSpawnAt = 0;
function spawnTick() {
  const now = performance.now();
  if (cloudParadeCheckbox.checked && now >= nextSpawnAt && activeClouds.length < CLOUD_CAP) {
    spawnCloud();
    // Below the floor (2 active) we re-spawn fast so the sky catches up.
    // At or above the floor we space them out so the cap doesn't get
    // hit too quickly. Lifetime is 18–32s; with these gaps the steady
    // state hovers around 3 visible clouds.
    if (activeClouds.length < CLOUD_FLOOR) {
      nextSpawnAt = now + 700 + Math.random() * 1200;   // 0.7–1.9 s
    } else {
      nextSpawnAt = now + 4000 + Math.random() * 3500;  // 4–7.5 s
    }
  }
  setTimeout(spawnTick, 500);
}
spawnTick();

// ── FX layer (typewriter, spotlight, idle-sway, parallax) ────────────────
const typewriterCheckbox = document.getElementById('setting-typewriter') as HTMLInputElement;
const spotlightCheckbox = document.getElementById('setting-spotlight') as HTMLInputElement;
const idleSwayCheckbox = document.getElementById('setting-idle-sway') as HTMLInputElement;
const parallaxCheckbox = document.getElementById('setting-parallax') as HTMLInputElement;
const cloudPeekFullCheckbox = document.getElementById('setting-cloud-peek-full') as HTMLInputElement;
const labelsCheckbox = document.getElementById('setting-labels') as HTMLInputElement;
const paperDustCheckbox = document.getElementById('setting-paper-dust') as HTMLInputElement;
const stageVignetteCheckbox = document.getElementById('setting-stage-vignette') as HTMLInputElement;
const masksCheckbox = document.getElementById('setting-masks') as HTMLInputElement;
const hazeCheckbox = document.getElementById('setting-haze') as HTMLInputElement;
const globalHazeCheckbox = document.getElementById('setting-global-haze') as HTMLInputElement;
const godRaysCheckbox = document.getElementById('setting-god-rays') as HTMLInputElement;
const bgSoftenCheckbox = document.getElementById('setting-bg-soften') as HTMLInputElement;
const spotlightEl = document.getElementById('speaker-spotlight')!;

typewriterCheckbox.checked = fxSettings.typewriter;
spotlightCheckbox.checked = fxSettings.spotlight;
idleSwayCheckbox.checked = fxSettings.idleSway;
parallaxCheckbox.checked = fxSettings.parallax;
cloudPeekFullCheckbox.checked = fxSettings.cloudPeekFullWidth;
labelsCheckbox.checked = fxSettings.showLabels;

function syncSpotlightVisibility() {
  spotlightEl.classList.toggle('hidden', !fxSettings.spotlight);
  if (!fxSettings.spotlight) spotlightEl.classList.remove('active');
}
syncSpotlightVisibility();

typewriterCheckbox.addEventListener('change', () => {
  fxSettings.typewriter = typewriterCheckbox.checked;
});
spotlightCheckbox.addEventListener('change', () => {
  fxSettings.spotlight = spotlightCheckbox.checked;
  syncSpotlightVisibility();
});
idleSwayCheckbox.addEventListener('change', () => {
  fxSettings.idleSway = idleSwayCheckbox.checked;
});
parallaxCheckbox.addEventListener('change', () => {
  fxSettings.parallax = parallaxCheckbox.checked;
});
cloudPeekFullCheckbox.addEventListener('change', () => {
  fxSettings.cloudPeekFullWidth = cloudPeekFullCheckbox.checked;
});
labelsCheckbox.addEventListener('change', () => {
  fxSettings.showLabels = labelsCheckbox.checked;
  stage.repaintAllSprites();
});

// Paper-dust particles — spawn once, then toggle the container's
// .hidden class from the debug checkbox.
const paperDustEl = document.getElementById('paper-dust')!;
paperDustCheckbox.checked = fxSettings.paperDust;
function syncPaperDust() {
  paperDustEl.classList.toggle('hidden', !fxSettings.paperDust);
}
syncPaperDust();
paperDustCheckbox.addEventListener('change', () => {
  fxSettings.paperDust = paperDustCheckbox.checked;
  syncPaperDust();
});

const stageVignetteEl = document.getElementById('stage-vignette')!;
stageVignetteCheckbox.checked = fxSettings.stageVignette;
function syncStageVignette() {
  stageVignetteEl.classList.toggle('hidden', !fxSettings.stageVignette);
}
syncStageVignette();
stageVignetteCheckbox.addEventListener('change', () => {
  fxSettings.stageVignette = stageVignetteCheckbox.checked;
  syncStageVignette();
});

masksCheckbox.checked = fxSettings.showMasks;
stage.setMasksVisible(fxSettings.showMasks);
masksCheckbox.addEventListener('change', () => {
  fxSettings.showMasks = masksCheckbox.checked;
  stage.setMasksVisible(masksCheckbox.checked);
});

const hazeEl = document.getElementById('atmospheric-haze')!;
const godRaysEl = document.getElementById('god-rays')!;
hazeCheckbox.checked = fxSettings.haze;
godRaysCheckbox.checked = fxSettings.godRays;
function syncHaze()    { hazeEl.classList.toggle('hidden', !fxSettings.haze); }
function syncGodRays() { godRaysEl.classList.toggle('hidden', !fxSettings.godRays); }
syncHaze();
syncGodRays();
hazeCheckbox.addEventListener('change', () => {
  fxSettings.haze = hazeCheckbox.checked;
  syncHaze();
});

const globalHazeEl = document.getElementById('global-haze')!;
globalHazeCheckbox.checked = fxSettings.globalHaze;
function syncGlobalHaze() {
  globalHazeEl.classList.toggle('hidden', !fxSettings.globalHaze);
}
syncGlobalHaze();
globalHazeCheckbox.addEventListener('change', () => {
  fxSettings.globalHaze = globalHazeCheckbox.checked;
  syncGlobalHaze();
});
godRaysCheckbox.addEventListener('change', () => {
  fxSettings.godRays = godRaysCheckbox.checked;
  syncGodRays();
});

const bgCanvasEl = document.getElementById('stage-bg')!;
bgSoftenCheckbox.checked = fxSettings.bgSoften;
function syncBgSoften() {
  bgCanvasEl.classList.toggle('softened', fxSettings.bgSoften);
}
syncBgSoften();
bgSoftenCheckbox.addEventListener('change', () => {
  fxSettings.bgSoften = bgSoftenCheckbox.checked;
  syncBgSoften();
});

(function spawnPaperDust() {
  const FLECK_COUNT = 32;
  for (let i = 0; i < FLECK_COUNT; i++) {
    const fleck = document.createElement('div');
    fleck.className = 'fleck';
    // Start anywhere across the lower 80% of the viewport.
    fleck.style.top   = `${20 + Math.random() * 80}%`;
    fleck.style.left  = `${Math.random() * 100}%`;
    // Random size 1.5–4px.
    const size = 1.5 + Math.random() * 2.5;
    fleck.style.setProperty('--fleck-size', `${size.toFixed(2)}px`);
    // 22–48s drift duration so they don't sync up.
    fleck.style.setProperty('--fleck-dur', `${(22 + Math.random() * 26).toFixed(1)}s`);
    // Negative animation-delay so the loop starts mid-cycle — no
    // synchronized fade-in at page load.
    fleck.style.setProperty('--fleck-delay', `-${(Math.random() * 30).toFixed(1)}s`);
    // Drift mostly upward and slightly across; direction varies.
    const dirSign = Math.random() < 0.5 ? -1 : 1;
    const dx = dirSign * (15 + Math.random() * 35);   // -50..-15 or 15..50 vw
    const dy = -(15 + Math.random() * 25);            // -40..-15 vh (upward)
    fleck.style.setProperty('--fleck-dx', `${dx.toFixed(1)}vw`);
    fleck.style.setProperty('--fleck-dy', `${dy.toFixed(1)}vh`);
    paperDustEl.appendChild(fleck);
  }
})();

// Mouse parallax — feed normalized mouse position into the stage so the
// camera (and therefore every layer with depth) shifts subtly. Closer
// layers move more in screen space than distant ones, selling the box.
document.addEventListener('mousemove', e => {
  const nx = (e.clientX / window.innerWidth)  * 2 - 1;   // -1..1
  const ny = (e.clientY / window.innerHeight) * 2 - 1;
  // Invert Y so mouse-up moves camera up (matches natural head tilt).
  stage.setMouseParallaxTarget(nx, -ny);
});
document.addEventListener('mouseleave', () => {
  stage.setMouseParallaxTarget(0, 0);
});

// Spotlight follow. Game pings us each time a voice line starts; we lerp
// the CSS variable toward the speaker's projected screen X every frame
// so the warm pool tracks them through `move` beats and exits.
let spotlightTargetVoice: VoiceKey | null = null;
let spotlightTargetX = 0.5;   // 0..1
let spotlightX = 0.5;
game.onLineStart = (voice) => {
  spotlightTargetVoice = voice;
  if (fxSettings.spotlight) spotlightEl.classList.add('active');
};

function tickSpotlight() {
  if (fxSettings.spotlight && spotlightTargetVoice) {
    const sx = stage.getSpriteScreenX(spotlightTargetVoice);
    if (sx !== null) spotlightTargetX = Math.max(0.05, Math.min(0.95, sx));
    spotlightX += (spotlightTargetX - spotlightX) * 0.08;
    spotlightEl.style.setProperty('--sp-x', `${(spotlightX * 100).toFixed(2)}%`);
  }
  requestAnimationFrame(tickSpotlight);
}
tickSpotlight();

// Replay button on the end screen — full reload is the simplest reliable
// way to reset the AudioContext, scene state, and beat cursor.
document.getElementById('end-replay')?.addEventListener('click', () => {
  track('replay_clicked');
  location.reload();
});

// ── Share card on the end screen ─────────────────────────────────────────
// Two buttons live next to "begin again":
//   - share on x  → downloads PNG, opens x.com/intent/post in new tab
//   - download    → downloads PNG only
// X intent URLs cannot accept image attachments (Twitter removed that
// years ago), so the flow is: the user gets the file on disk, then drags
// or pastes it into the tweet compose window that opened automatically.
// The hint line below the buttons tells them to do this.
import('./share-card.ts').then(({ generateShareCard, buildTweetIntentUrl, downloadBlob, defaultCardFilename }) => {
  const xBtn = document.getElementById('end-share-x') as HTMLButtonElement | null;
  const dlBtn = document.getElementById('end-share-download') as HTMLButtonElement | null;
  const hintEl = document.getElementById('end-share-hint') as HTMLElement | null;
  if (!xBtn || !dlBtn) return;

  const flashHint = (text: string) => {
    if (!hintEl) return;
    hintEl.classList.remove('flash');
    hintEl.textContent = text;
    void hintEl.offsetWidth;
    hintEl.classList.add('flash');
  };

  const buildAndDownload = async (): Promise<Blob | null> => {
    const data = game.getShareData();
    if (!data) {
      flashHint('no ending reached yet');
      return null;
    }
    try {
      const blob = await generateShareCard(data);
      downloadBlob(blob, defaultCardFilename(data));
      track('share_card_downloaded', { storyId: data.endingKey });
      return blob;
    } catch (err) {
      console.error('[share] card generation failed', err);
      flashHint('couldn\'t render the card — try again');
      return null;
    }
  };

  xBtn.addEventListener('click', async () => {
    xBtn.disabled = true;
    dlBtn.disabled = true;
    const blob = await buildAndDownload();
    xBtn.disabled = false;
    dlBtn.disabled = false;
    if (!blob) return;
    const data = game.getShareData();
    if (!data) return;
    // Game is hosted at the deployed origin — the card embeds the URL
    // visually; the tweet uses it as the playable link.
    const gameUrl = window.location.origin + window.location.pathname.replace(/\/[^/]*$/, '/');
    const tweetUrl = buildTweetIntentUrl(data, gameUrl);
    window.open(tweetUrl, '_blank', 'noopener,noreferrer');
    track('share_x_opened', { storyId: data.endingKey });
    flashHint('image downloaded — attach it to your tweet');
  });

  dlBtn.addEventListener('click', async () => {
    dlBtn.disabled = true;
    xBtn.disabled = true;
    const blob = await buildAndDownload();
    dlBtn.disabled = false;
    xBtn.disabled = false;
    if (blob) flashHint('card downloaded');
  });
});

